import React, { Suspense, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { CrashReportApi, CrashReportOccurrenceApi } from "../../../../../../../../services/Api/Admin/adminTypes";
import { mapResponseToResponseDataWithId } from "../../../../services/mapResponseToResponseDataWithId";
import SubmenuCrashReportsNavigation from "../../components/SubmenuCrashReportsNavigation";
import CrashReportMarkAsPermanentlyBrokenModal from "../Index/pages/Edit/components/CrashReportMarkAsPermanentlyBrokenModal";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "crash_report_occurrences";
const resourceType = "crash_report_occurrences";

const List = ({ resource }: ListState) => {
	const [items, setItems] = useState<CrashReportOccurrenceApi[]>([]);
	const multipleActionsParamsRef = useRef<MultipleActionsParams>();
	const areAllItemsSelectedRef = useRef<boolean>();
	const { t } = useTranslation("admin");
	const location = useLocation();
	const segmentContextValue = React.useContext(SegmentContext);
	const [params, setParams] = useState<Record<string, any>>({});
	const { addSuccessFlash } = useFlash();
	const [crashReportIdsToMarkAsPermanentlyBroken, setCrashReportIdsToMarkAsPermanentlyBroken] = useState<number[]>(
		[]
	);

	if (!resource) return null;
	const data = resource.read();

	const handleRefresh = async () => {
		if (config.fetch) {
			const res = await config.fetch(params);
			setItems(res);
		}
	};

	const handleCloseCrashReports = async (
		ids: number[],
		listParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		await adminApi.closeCrashReports(ids, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const handleRestoreCrashReports = async (
		ids: number[],
		listParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		await adminApi.restoreCrashReports(ids, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const handleStartFixingCrashReports = async (
		ids: number[],
		listParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		await adminApi.startFixingCrashReports(ids, multipleActionParams, areAllItemsSelected);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const handleCloseCrashReport = async (crashReportId: number) => {
		await adminApi.closeCrashReport(crashReportId);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const handleRestoreCrashReport = async (crashReportId: number) => {
		await adminApi.restoreCrashReport(crashReportId);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const handleStartFixingCrashReport = async (crashReportId: number) => {
		await adminApi.startFixCrashReport(crashReportId);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	let config = {
		fields: [
			{
				id: "app_name",
				name: t("modules.crash_report.field.app_name.title"),
				render: (data: CrashReportOccurrenceApi) => (
					<Link to={`/admin/crash_reports/occurrences/${data.id}`}>{data.app_name}</Link>
				),
				renderExport: (data: CrashReportOccurrenceApi) => data.app_name,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsAppNamesSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "app_version",
				name: t("common.word.app_version"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsAppVersionsSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "created_at",
				name: t("modules.crash_report.field.created_at.title"),
				type: "date",
				render: (data: CrashReportOccurrenceApi) => FormatDate(data.created_at),
			},
			{
				id: "terminal",
				name: t("common.word.terminal"),
				render: (item: CrashReportOccurrenceApi) => item.terminal && `${item.terminal}`,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsTerminalsSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
			{
				id: "organization_name",
				name: t("lib:common.word.venue"),
				render: (item: CrashReportApi) => {
					const id = item.organization_name?.substring(
						item.organization_name.indexOf("(") + 1,
						item.organization_name.indexOf(")")
					);
					const name = id ? item.organization_name.split(`(${id}`)[0] : item.organization_name;
					return (
						<div className="d-flex flex-column">
							{name}
							{id && <small className="text-muted">{id.split("=").join(" ")}</small>}
						</div>
					);
				},
				renderExport: (item: CrashReportApi) => item.organization_name && `${item.organization_name}`,
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						return mapResponseToResponseDataWithId(
							await adminApi.getCrashReportsOrganizationNamesSearchSelect(search, params, {
								cancelToken: options?.token,
							})
						);
					},
				},
			},
		],
		selectedColumns: ["app_name", "app_version", "created_at", "terminal", "organization_name"],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: CrashReportOccurrenceApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (item: CrashReportOccurrenceApi) => handleCloseCrashReport(item.crash_report_id),
				visible: (item: CrashReportOccurrenceApi) => item?.crash_report?.status !== "ARCHIVED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (item: CrashReportOccurrenceApi) => handleRestoreCrashReport(item.crash_report_id),
				visible: (item: CrashReportOccurrenceApi) => item?.crash_report?.status !== "OCCURRING",
				refreshListAfter: true,
			},
			{
				name: t("modules.crash_report.action.start_fix.title"),
				click: (item: CrashReportOccurrenceApi) => handleStartFixingCrashReport(item.crash_report_id),
				visible: (item: CrashReportOccurrenceApi) =>
					item?.crash_report?.status !== "ARCHIVE" && item?.crash_report?.status !== "WORK_IN_PROGRESS",
				refreshListAfter: true,
			},
			{
				name: t("modules.crash_report.action.mark_as_permanently_broken.title"),
				click: (item: CrashReportOccurrenceApi) =>
					setCrashReportIdsToMarkAsPermanentlyBroken([item.crash_report_id]),
				visible: (item: CrashReportOccurrenceApi) => item?.crash_report?.status !== "PERMANENTLY_BROKEN",
			},
		],
		multipleActions: [
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (
					items: CrashReportOccurrenceApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) =>
					handleCloseCrashReports(
						items.map((item) => item.crash_report_id),
						listParams,
						areAllItemsSelected,
						multipleActionParams
					),
				visible: (items: CrashReportOccurrenceApi[]) =>
					items.filter((item) => item?.crash_report?.status !== "ARCHIVED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (
					items: CrashReportOccurrenceApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) =>
					handleRestoreCrashReports(
						items.map((item) => item.crash_report_id),
						listParams,
						areAllItemsSelected,
						multipleActionParams
					),
				visible: (items: CrashReportOccurrenceApi[]) =>
					items.filter((item) => item?.crash_report?.status === "ARCHIVED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("modules.crash_report.action.start_fix.title"),
				click: (
					items: CrashReportOccurrenceApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) =>
					handleStartFixingCrashReports(
						items.map((item) => item.crash_report_id),
						listParams,
						areAllItemsSelected,
						multipleActionParams
					),
				visible: (item: CrashReportOccurrenceApi) =>
					item?.crash_report?.status !== "ARCHIVED" && item?.crash_report?.status !== "WORK_IN_PROGRESS",
				refreshListAfter: true,
			},
			{
				name: t("modules.crash_report.action.mark_as_permanently_broken.title"),
				click: (
					multipleActionsItems: CrashReportOccurrenceApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					setCrashReportIdsToMarkAsPermanentlyBroken(items.map((item) => item.crash_report_id));
					multipleActionsParamsRef.current = multipleActionParams;
					areAllItemsSelectedRef.current = Boolean(areAllItemsSelected);
				},
				visible: (item: CrashReportOccurrenceApi) => item?.crash_report?.status !== "PERMANENTLY_BROKEN",
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params = {
				...params,
				include: "crash_report",
			};
			setParams(params);
			return adminApi.getCrashReportsOccurrences(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.crash_report.field.occurrences.header.title"),
			filename: t("modules.crash_report.field.occurrences.field.export_config.filename"),
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;

	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return (
		<>
			{crashReportIdsToMarkAsPermanentlyBroken.length > 0 && (
				<CrashReportMarkAsPermanentlyBrokenModal
					isShown={crashReportIdsToMarkAsPermanentlyBroken.length > 0}
					onHide={() => setCrashReportIdsToMarkAsPermanentlyBroken([])}
					ids={crashReportIdsToMarkAsPermanentlyBroken}
					handleSuccessAction={handleRefresh}
					multipleActionsParams={multipleActionsParamsRef.current}
					areAllItemsSelected={areAllItemsSelectedRef.current}
				/>
			)}
			<ListData data={items} config={config} onFetch={(items: CrashReportOccurrenceApi[]) => setItems(items)} />
		</>
	);
};

const CrashReportsOccurrencesIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<any>();
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const isMobile = useWindowSize().isMobile;

	useEffect(() => {
		handleChangeTabTitle(
			`${t("modules.crash_report.field.occurrences.header.title")} | ${t("lib:go_component.navigation.admin")}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.crash_report.field.occurrences.header.title")} />}
			<SubmenuCrashReportsNavigation />
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default CrashReportsOccurrencesIndexPage;
